import { Deficode, Hoofdcode } from "@/models/hoofdcode";
import { OvereenkomstHouder } from "@/models/ovk";
import { Subwerkcat } from "@/models/subwerkcat";
import { Werkcat } from "@/models/werkcat";
import { format } from "date-fns";

export interface ZoekCategorie {
    value: 'bestek' | 'algemene_info' | 'hoofdcodes' | 'bepalingen';
    label: string;
}

export const ZOEK_CATEGORIEEN: ZoekCategorie[] = [
    { value: 'bestek', label: 'Bestek gegevens' },
    { value: 'algemene_info', label: 'Algemene informatie' },
    { value: 'hoofdcodes', label: 'Hoofdcodes' },
    { value: 'bepalingen', label: 'Bepalingen' }
];

export interface BestekFilterParams {
    // UPDATED: beschrijving is nu een array van strings
    beschrijving: string[];
    zoek_categorien: ZoekCategorie[];
    provincie: string | null;
    gemeente: string | null;
    woonplaats: string | null;
    jaarversie: string | null;
    // NIEUW: "release versie" (Bestek.versie, bv. "1.9 15/04/2020") — los van jaarversie,
    // cascadeert eronder (zie bestek-filter.tsx).
    release: string | null;
    aanmaak_datum_vanaf: Date | undefined;
    aanmaak_datum_tm: Date | undefined;
    wijzigings_datum_vanaf: Date | undefined;
    wijzigings_datum_tm: Date | undefined;
    hoofdcodes_werkcats: Werkcat[],
    hoofdcodes_subwerkcats: Subwerkcat[],
    hoofdcodes: Hoofdcode[],
    deficodes: Deficode[],
    ovks: OvereenkomstHouder[]
}

export const initialBestekFilterParams: BestekFilterParams = {
    beschrijving: [], // UPDATED: nu een lege array
    zoek_categorien: [ZOEK_CATEGORIEEN[0]], // Default alleen 'bestek'
    hoofdcodes_werkcats: [],
    hoofdcodes_subwerkcats: [],
    hoofdcodes: [],
    deficodes: [],
    ovks: [],
    provincie: null,
    gemeente: null,
    woonplaats: null,
    jaarversie: null,
    release: null,
    aanmaak_datum_vanaf: undefined,
    aanmaak_datum_tm: undefined,
    wijzigings_datum_vanaf: undefined,
    wijzigings_datum_tm: undefined
}

export const mapFilterParamsToQueryParams = (params: BestekFilterParams): { [key: string]: any } => {
    const query_params: { [key: string]: any } = {};
    Object.keys(params).forEach((k) => {
        const [ query_key, query_value ] = mapFilterValueToQueryParam(k as keyof BestekFilterParams, params[k as keyof BestekFilterParams]);
        if (query_key !== null) {
            if (query_value !== null) {
                query_params[query_key] = query_value;
            } else if (query_key in query_params) {
                delete query_params[query_key];
            }
        }
    });

    return query_params;
}

export const mapFilterValueToQueryParam = (key: keyof BestekFilterParams, value: any): [string | null, any] => {
    const textFields: (keyof BestekFilterParams)[] = ["provincie", "gemeente", "woonplaats", "jaarversie", "release"];
    const dateFields: (keyof BestekFilterParams)[] = ["wijzigings_datum_vanaf", "wijzigings_datum_tm", "aanmaak_datum_vanaf", "aanmaak_datum_tm"];
    const idFields: (keyof BestekFilterParams)[] = ["hoofdcodes", "hoofdcodes_werkcats", "hoofdcodes_subwerkcats", "ovks", "deficodes"];
    const keyRenameMap: { [key: string]: string } = {
        "jaarversie": "versie_jaarversie",
        "release": "versie",
        "hoofdcodes": "hoofdcode_ids",
        "deficodes": "deficode_ids",
        "hoofdcodes_werkcats": "hoofdcode_werk_categorie_ids",
        "hoofdcodes_subwerkcats": "hoofdcode_subwerk_categorie_ids",
        "ovks": "overeenkomst_houder_ids"
    };

    // HANDLING VOOR ZOEK_CATEGORIEEN
    if (key === 'zoek_categorien') {
        const categories = value as ZoekCategorie[];
        if (!categories || categories.length === 0) {
            return ['zoek_categorien', ['bestek']]; // Default fallback
        }
        return ['zoek_categorien', categories.map(c => c.value)];
    }

    // UPDATED: HANDLING VOOR BESCHRIJVING ARRAY
    if (key === 'beschrijving') {
        const terms = value as string[];
        if (!terms || terms.length === 0) {
            return ['beschrijving', null];
        }
        // Stuur elk term mee als object met type
        return ['beschrijving', terms.map(term => {
            const isExact = term.startsWith('"') && term.endsWith('"') && term.length > 2;
            return {
                value: isExact ? term.slice(1, -1) : term,
                exact: isExact
            };
        })];
    }

    const query_key: string = key in keyRenameMap ? keyRenameMap[key] : key;

    if (textFields.includes(key)) {
        return [query_key, value && value.length > 0 ? value : null];
    }

    if (dateFields.includes(key)) {
        return [query_key, value ? format(value as Date, 'yyyy-MM-dd') : null];
    }

    if (idFields.includes(key)) {
        const data = value as { id: number, alle_deficode_ids?: number[] }[];
        if (!data || data.length === 0) {
            return [query_key, null];
        }
    
        if (key === 'deficodes') {
            // Gebruik alle_deficode_ids als die beschikbaar zijn
            const ids = data.flatMap(d => d.alle_deficode_ids && d.alle_deficode_ids.length > 0 ? d.alle_deficode_ids : [d.id]);
            return [query_key, ids];
        }
    
        return [query_key, data.map(d => d.id)];
    }

    return [null, null]
}